import { stageLabel } from "@staffsmith/api-types/labels";
import type { ReactNode } from "react";
import { ApiError } from "./api.js";

// The few building blocks the pages share. Plain HTML elements, styled in
// styles.css; no component library yet (docs/engineering-conventions.md).

export function Page({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="page">
      <header className="page-header">
        <h1>{title}</h1>
        {actions ? <div className="page-actions">{actions}</div> : null}
      </header>
      {children}
    </section>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  if (error instanceof ApiError) {
    return (
      <div className="error" role="alert">
        <strong>{error.title}</strong>
        {error.detail ? <span> {error.detail}</span> : null}
        {error.errors.length ? (
          <ul>
            {error.errors.map((e) => (
              <li key={`${e.field}:${e.message}`}>
                {e.field}: {e.message}
              </li>
            ))}
          </ul>
        ) : null}
        {error.requestId ? <small> Request {error.requestId}</small> : null}
      </div>
    );
  }
  return (
    <div className="error" role="alert">
      {error instanceof Error ? error.message : String(error)}
    </div>
  );
}

export const Loading = () => <p className="muted">Loading…</p>;

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted">{children}</p>;
}

export const label = (value: string) => value.replaceAll("_", " ");

export function Badge({ value }: { value: string }) {
  return <span className={`badge badge-${value}`}>{label(value)}</span>;
}

// A submission stage as people read it ("interviewing", not "interview"; design v0.6, C2).
export function StageBadge({ stage }: { stage: string | null | undefined }) {
  return <span className={`badge badge-${stage ?? ""}`}>{stageLabel(stage)}</span>;
}

export const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

// Money shows as currency with two decimals (Nathan, Nov 2, 2026). A cost
// under a cent, such as one AI call, reads "under $0.01" rather than "$0.00".
export const formatMoney = (m: { amount: string | number; currency: string } | undefined) => {
  if (!m) return "—";
  const n = Number(m.amount);
  const format = (v: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: m.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);
  return n > 0 && n < 0.005 ? `under ${format(0.01)}` : format(n);
};

export const formatUsd = (amount: string | number | null | undefined) =>
  amount == null || amount === "" ? "—" : formatMoney({ amount, currency: "USD" });

// Reads a form's fields as strings, dropping empty ones.
export function formValues(form: HTMLFormElement): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of new FormData(form).entries()) {
    if (typeof v === "string" && v.trim() !== "") out[k] = v.trim();
  }
  return out;
}
